import { useState, type FormEvent } from 'react';

import { SEARCH_STABILITY_DEFAULT_LIMIT, SearchStabilityRequestSchema, type SearchStabilityPair, type SearchStabilityResponse, type SearchStabilityRun } from '@clm/shared-types';

import { fetchStability } from '../../api/search-quality';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { ErrorNotice } from '../../components/ErrorNotice';
import { panelProblemAction } from './panel-action';
import { toProblem, type Panel } from './panel-state';

/**
 * Stability (O-6) — the same query must rank the same way for the same stored
 * rows · CLM-39.
 *
 * A form (query text, runs) posts `search-stability`; the table lists the
 * logged runs newest first with each row's drift against the older run: "—",
 * "drift (tuned since)" or, in the bad tone, "drift — no tune recorded". The
 * report compares **logged** outcomes and performs no search, so it can never
 * itself introduce nondeterminism. `tuned_since` is a hint, not a proof — the
 * copy says why — and the one tile counts the pairs that drifted with no tune
 * recorded.
 */

type FormState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'field_errors'; readonly messages: readonly string[] }
  | { readonly kind: 'busy' };

function driftLabel(pair: SearchStabilityPair | undefined): { readonly text: string; readonly tone: 'bad' | 'plain' } {
  if (pair === undefined || !pair.drift) return { text: '—', tone: 'plain' };
  return pair.tuned_since ? { text: 'drift (tuned since)', tone: 'plain' } : { text: 'drift — no tune recorded', tone: 'bad' };
}

export function StabilityPanel() {
  const [queryText, setQueryText] = useState('');
  const [limit, setLimit] = useState(String(SEARCH_STABILITY_DEFAULT_LIMIT));
  const [form, setForm] = useState<FormState>({ kind: 'idle' });
  const [panel, setPanel] = useState<Panel<SearchStabilityResponse> | null>(null);

  function run() {
    const parsed = SearchStabilityRequestSchema.safeParse({ query_text: queryText, limit: limit.trim() === '' ? undefined : Number(limit) });
    if (!parsed.success) {
      setForm({
        kind: 'field_errors',
        messages: parsed.error.issues.map((issue) => {
          const field = issue.path.join('.');
          if (field === 'query_text') return 'Query: 1–400 characters, as it was typed on Search.';
          if (field === 'limit') return 'Runs: a whole number from 2 to 50.';
          return `${field}: ${issue.message}`;
        }),
      });
      return;
    }
    setForm({ kind: 'busy' });
    setPanel({ kind: 'loading' });
    void (async () => {
      try {
        const response = await fetchStability(parsed.data);
        setPanel({ kind: 'ready', response });
      } catch (error) {
        setPanel(toProblem(error));
      } finally {
        setForm({ kind: 'idle' });
      }
    })();
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    run();
  }

  const response = panel?.kind === 'ready' ? panel.response : null;
  const pairByNewer = new Map<string, SearchStabilityPair>((response?.pairs ?? []).map((pair) => [pair.to_search_id, pair]));

  const columns: readonly DataTableColumn<SearchStabilityRun>[] = [
    { id: 'run', header: 'Run', cell: (run) => <code>{run.search_id}</code> },
    { id: 'when', header: 'When', cell: (run) => run.occurred_utc },
    { id: 'effective', header: 'Effective query', cell: (run) => run.effective_query },
    { id: 'top', header: 'Top clause', cell: (run) => (run.top_clause_code === null ? <span className="mut">—</span> : <code>{run.top_clause_code}</code>) },
    { id: 'score', header: 'Top score', cellClassName: 'num', cell: (run) => (run.top_score === null ? '—' : run.top_score.toFixed(4)) },
    { id: 'results', header: 'Results', cellClassName: 'num', cell: (run) => String(run.result_count) },
    {
      id: 'drift',
      header: 'Drift',
      cell: (run) => {
        const label = driftLabel(pairByNewer.get(run.search_id));
        return (
          <span className="sq-drift" data-tone={label.tone}>
            {label.text}
          </span>
        );
      },
    },
  ];

  return (
    <section className="card" aria-labelledby="sq-stability">
      <h3 id="sq-stability">Stability (O-6)</h3>
      <p>
        The same query must rank the same way for the same stored rows (O-6). A run pair is marked &lsquo;tuned since&rsquo; when a
        judgement, boost or alias changed between the two runs. Drift with no tune recorded can also come from re-embedding, a
        different query vector, or a τ/λ_f change — check those before treating it as a defect.
      </p>

      <form className="sq-form" aria-labelledby="sq-stability-form" onSubmit={submit}>
        <h4 id="sq-stability-form">Compare logged runs</h4>
        <label>
          Query text
          <input type="text" value={queryText} required maxLength={400} onChange={(e) => setQueryText(e.target.value)} />
        </label>
        <label>
          Runs (2 to 50)
          <input type="number" value={limit} min={2} max={50} step={1} onChange={(e) => setLimit(e.target.value)} />
        </label>
        <div className="sq-actions">
          <button type="submit" className="btn" disabled={form.kind === 'busy'}>
            Compare
          </button>
        </div>
        {form.kind === 'field_errors' ? (
          <ul role="alert" className="mut">
            {form.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ) : null}
      </form>

      {panel?.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading logged runs…
        </p>
      ) : null}

      {panel?.kind === 'problem' ? (
        <ErrorNotice
          kind={panel.problem}
          detail={panel.detail}
          correlation_id={panel.correlation_id}
          action={panelProblemAction(panel.problem, 'search-stability (POST /v1/search/stability)', run)}
        />
      ) : null}

      {response ? (
        <>
          <div className="totals">
            <div className="total" data-tone={response.unexplained_drift_count > 0 ? 'bad' : 'plain'}>
              <span>Drift with no tune recorded</span>
              <b>{response.unexplained_drift_count}</b>
            </div>
          </div>

          <DataTable
            caption="Logged rankings for one query, newest first"
            columns={columns}
            rows={response.runs}
            rowKey={(run) => run.search_id}
            empty={
              <>
                <p>
                  <strong>This query has not been searched.</strong>
                </p>
                <p>Run it on Search first.</p>
              </>
            }
          />
        </>
      ) : null}
    </section>
  );
}
